import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Play, Mail, Phone, MapPin, Instagram, Twitter, Facebook, Youtube, Music, Users, Film, Heart, ChevronRight, Star, Clock, Calendar, Linkedin, Globe, ExternalLink, Volume2, VolumeX } from "lucide-react";
import AdminPanel from "./AdminPanel";
import DataRoom from "./DataRoom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SITE UNDER CONSTRUCTION - Set to true to show construction page
const UNDER_CONSTRUCTION = false;

// Under Construction Page
const UnderConstructionPage = () => (
  <div className="under-construction">
    <div className="construction-content">
      <img 
        src="/tallawah-logo-gold.png" 
        alt="Tallawah Show Logo" 
        className="construction-logo"
      />
      <h1>SITE UNDER CONSTRUCTION</h1>
      <div className="construction-line"></div>
      <p>We're working hard to bring you something amazing.</p>
      <p className="construction-subtitle">Coming Soon</p>
      <div className="construction-socials">
        <a href="https://www.instagram.com/tallawah.ja2us/" target="_blank" rel="noopener noreferrer"><Instagram size={28} /></a>
        <a href="https://www.facebook.com/profile.php?id=100083257970568" target="_blank" rel="noopener noreferrer"><Facebook size={28} /></a>
        <a href="https://www.youtube.com/channel/UCyuz1dtiIA3NwepDoeGlYkw" target="_blank" rel="noopener noreferrer"><Youtube size={28} /></a>
      </div>
    </div>
  </div>
);

// Logo component using the gold transparent logo
const Logo = ({ className = "" }) => (
  <img 
    src="/tallawah-logo-gold.png" 
    alt="Tallawah Show Logo" 
    className={`h-16 w-auto ${className}`}
  />
);

// TikTok Icon Component
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Helper function to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
  return match ? match[1] : null;
};

// Video with Custom Thumbnail Component
const VideoWithThumbnail = ({ youtubeId, thumbnail, name }) => {
  const [showVideo, setShowVideo] = useState(false);
  
  if (showVideo) {
    return (
      <div className="cast-video-embed">
        <iframe
          width="100%"
          height="200"
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
          title={`${name} Video`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  }
  
  return (
    <div className="cast-video-thumbnail" onClick={() => setShowVideo(true)}>
      <img src={thumbnail} alt={`${name} Video`} />
      <div className="video-play-overlay">
        <div className="video-play-button">
          <Play size={40} fill="white" />
        </div>
        <span>Watch Video</span>
      </div>
    </div>
  );
};

// Cast Member Modal Component
const CastMemberModal = ({ member, isOpen, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  
  if (!isOpen || !member) return null;

  const socialLinks = member.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v);
  const shareUrl = `${window.location.origin}/api/share/cast/${member.id}?v=2`;
  
  // Combine main image with gallery
  const allImages = [member.image_url, ...(member.gallery || [])].filter(Boolean);
  
  // Check for YouTube video
  const youtubeId = getYouTubeId(socialLinks.youtube);

  return (
    <div className="cast-modal-overlay" onClick={onClose} data-testid="cast-modal">
      <div className="cast-modal" onClick={e => e.stopPropagation()}>
        <button className="cast-modal-close" onClick={onClose} data-testid="cast-modal-close">
          <X size={24} />
        </button>
        
        <div className="cast-modal-content">
          {/* Image Section with Gallery */}
          <div className="cast-modal-image-section">
            <div className="cast-modal-main-image">
              {allImages.length > 0 ? (
                <img 
                  src={allImages[selectedImage]} 
                  alt={member.name}
                  style={{ objectPosition: member.image_position || 'center top' }}
                />
              ) : (
                <div className="cast-modal-placeholder">
                  <Users size={80} />
                </div>
              )}
            </div>
            
            {/* Gallery Thumbnails */}
            {allImages.length > 1 && (
              <div className="cast-gallery-thumbnails">
                {allImages.map((img, idx) => (
                  <div 
                    key={idx} 
                    className={`cast-gallery-thumb ${idx === selectedImage ? 'active' : ''}`}
                    onClick={() => setSelectedImage(idx)}
                    onMouseEnter={() => setSelectedImage(idx)}
                  >
                    <img src={img} alt={`${member.name} ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Info Section */}
          <div className="cast-modal-info">
            <h2 className="cast-modal-name">{member.name}</h2>
            <p className="cast-modal-role">{member.role}</p>
            
            {/* YouTube Video Embed with Custom Thumbnail */}
            {youtubeId && (
              <VideoWithThumbnail 
                youtubeId={youtubeId} 
                thumbnail={member.image_url} 
                name={member.name}
              />
            )}
            
            {/* Full Bio */}
            <div className="cast-modal-bio">
              <p>{member.full_bio || member.bio}</p>
            </div>
            
            {/* Social Links */}
            {hasSocials && (
              <div className="cast-modal-socials">
                <h4>Connect</h4>
                <div className="cast-social-links">
                  {socialLinks.website && (
                    <a href={socialLinks.website} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-website">
                      <Globe size={20} />
                      <span>Website</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-instagram">
                      <Instagram size={20} />
                      <span>Instagram</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.youtube && (
                    <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-youtube">
                      <Youtube size={20} />
                      <span>YouTube</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.tiktok && (
                    <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-tiktok">
                      <TikTokIcon size={20} />
                      <span>TikTok</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.twitter && (
                    <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-twitter">
                      <Twitter size={20} />
                      <span>Twitter</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.facebook && (
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-facebook">
                      <Facebook size={20} />
                      <span>Facebook</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {socialLinks.spotify && (
                    <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" className="cast-social-link" data-testid="social-spotify">
                      <Music size={20} />
                      <span>Spotify</span>
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            )}
            
            {/* Share Profile Section */}
            <div className="cast-share-section">
              <h4>Share Profile</h4>
              <div className="cast-share-buttons">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-btn share-facebook"
                  title="Share on Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`🇯🇲 ${member.name} - ${member.role}\n\nFrom TALLAWAH JAMAICANS IN NYC`)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-btn share-twitter"
                  title="Share on X/Twitter"
                >
                  <Twitter size={18} />
                </a>
                <a 
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`🇯🇲 *${member.name}* - ${member.role}\n\n${member.bio}\n\nFrom TALLAWAH JAMAICANS IN NYC\n\n${shareUrl}`)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-btn share-whatsapp"
                  title="Share on WhatsApp"
                >
                  <Phone size={18} />
                </a>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-btn share-linkedin"
                  title="Share on LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <a 
                  href={`https://www.instagram.com/`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-btn share-instagram"
                  title="Share on Instagram"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigator.clipboard.writeText(`🇯🇲 ${member.name} - ${member.role}\n\n${member.bio}\n\nFrom TALLAWAH JAMAICANS IN NYC\n@tallawah.ja2us\n\n${shareUrl}\n\n#TallawahShow #JamaicansInNYC #Reggae #Jamaica #NYC`);
                    alert('Caption + share link copied! Open Instagram and paste to share.');
                  }}
                >
                  <Instagram size={18} />
                </a>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const shareText = `🇯🇲 ${member.name} - ${member.role}\n\n${member.bio}\n\nFrom TALLAWAH JAMAICANS IN NYC\n\n${shareUrl}`;
                    navigator.clipboard.writeText(shareText);
                    alert('Profile info + share link copied to clipboard!');
                  }}
                  className="share-btn share-copy"
                  title="Copy Profile"
                >
                  <ExternalLink size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Featured Cast Card Component (for Home Page)
const FeaturedCastCard = ({ member, onClick }) => {
  const socialLinks = member.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v);
  const imagePosition = member.image_position || 'center top';
  
  return (
    <div className="cast-card clickable" onClick={onClick} data-testid={`cast-card-${member.name}`}>
      {member.image_url ? (
        <div className="cast-image-container h-48">
          <img 
            src={member.image_url} 
            alt={member.name} 
            className="cast-image" 
            style={{ objectPosition: imagePosition }}
          />
          <div className="cast-card-badge">View Profile</div>
        </div>
      ) : (
        <div className="cast-placeholder h-48">
          <Users size={60} />
        </div>
      )}
      <div className="p-4 text-center">
        <h3 className="font-bold text-lg gold-accent">{member.name}</h3>
        <p className="text-gray-400 text-sm">{member.role}</p>
        {member.bio && <p className="text-gray-500 text-xs mt-2 line-clamp-2">{member.bio}</p>}
        {hasSocials && (
          <div className="flex gap-2 mt-2 justify-center">
            {socialLinks.website && <Globe size={14} className="text-gray-500" />}
            {socialLinks.instagram && <Instagram size={14} className="text-gray-500" />}
            {socialLinks.youtube && <Youtube size={14} className="text-gray-500" />}
            {socialLinks.tiktok && <TikTokIcon size={14} className="text-gray-500" />}
          </div>
        )}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/cast', label: 'Cast & Crew' },
    { path: '/watch', label: 'Watch' },
    { path: '/support', label: 'Support' },
    { path: '/news', label: 'News' },
    { path: '/contact', label: 'Contact' },
    { path: '/dataroom', label: 'Data Room' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" data-testid="logo-link">
            <Logo className={`transition-all duration-300 ${scrolled ? 'h-12' : 'h-16'}`} />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu lg:hidden">
          <button 
            className="absolute top-6 right-6 text-white"
            onClick={() => setIsOpen(false)}
          >
            <X size={32} />
          </button>
          <Logo className="h-20 mb-8" />
          {navLinks.map(link => (
            <Link 
              key={link.path}
              to={link.path} 
              className="nav-link text-2xl"
              onClick={() => setIsOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

// Footer Component
const Footer = () => (
  <footer className="footer py-12">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <Logo className="h-16 mb-4" />
          <p className="text-gray-400 text-sm">Ten characters. One community. Infinite stories.</p>
        </div>
        <div>
          <h4 className="font-oswald text-lg gold-accent mb-4">QUICK LINKS</h4>
          <div className="flex flex-col gap-2">
            <Link to="/about" className="text-gray-400 hover:text-white transition-colors">About the Show</Link>
            <Link to="/cast" className="text-gray-400 hover:text-white transition-colors">Cast & Crew</Link>
            <Link to="/watch" className="text-gray-400 hover:text-white transition-colors">Watch Pilot</Link>
            <Link to="/support" className="text-gray-400 hover:text-white transition-colors">Get Involved</Link>
            <Link to="/dataroom" className="text-gray-400 hover:text-white transition-colors">Investor Data Room</Link>
          </div>
        </div>
        <div>
          <h4 className="font-oswald text-lg gold-accent mb-4">CONTACT</h4>
          <div className="flex flex-col gap-2 text-gray-400">
            <p>info@tallawahshow.com</p>
            <p>press@tallawahshow.com</p>
          </div>
        </div>
        <div>
          <h4 className="font-oswald text-lg gold-accent mb-4">FOLLOW US</h4>
          <div className="flex gap-4 flex-wrap">
            <a href="https://www.instagram.com/tallawah.ja2us/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-instagram"><Instagram size={24} /></a>
            <a href="https://x.com/TALLAWAHSHOW" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-twitter"><Twitter size={24} /></a>
            <a href="https://www.facebook.com/profile.php?id=100083257970568" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-facebook"><Facebook size={24} /></a>
            <a href="https://www.youtube.com/channel/UCyuz1dtiIA3NwepDoeGlYkw" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-youtube"><Youtube size={24} /></a>
            <a href="https://tiktok.com/@tallawah962" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-tiktok">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
            </a>
            <a href="https://linkedin.com/in/tallawah-show-64037b3b2" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors" data-testid="social-linkedin"><Linkedin size={24} /></a>
          </div>
        </div>
      </div>
      <div className="gold-line mb-8"></div>
      <div className="flex flex-col md:flex-row justify-between items-center text-gray-500 text-sm">
        <p>&copy; 2026 Tallawah Jamaicans in NYC. All rights reserved.</p>
        <p>Created by Charline Gager</p>
      </div>
    </div>
  </footer>
);

// Home Page
// Cinematic Curtain Intro — reveals the pilot episode on homepage load
const PILOT_VIDEO_ID = "hihlBQ0Bn84";
const CurtainIntro = ({ onClose }) => {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(false); // we start WITH sound the moment the viewer taps to enter
  const playerRef = useRef(null);
  const readyRef = useRef(false);

  // Build the YouTube IFrame API player. We create it muted so it is ready to
  // roll instantly, then unmute inside the viewer's "tap to enter" gesture —
  // the single browser-approved way to get sound on mobile.
  useEffect(() => {
    let cancelled = false;
    const createPlayer = () => {
      if (cancelled || !window.YT || !window.YT.Player) return;
      playerRef.current = new window.YT.Player("curtain-yt-player", {
        width: "100%",
        height: "100%",
        videoId: PILOT_VIDEO_ID,
        playerVars: { autoplay: 1, mute: 1, rel: 0, modestbranding: 1, playsinline: 1, cc_load_policy: 0, iv_load_policy: 3, controls: 0, showinfo: 0, disablekb: 1, fs: 0 },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            try { e.target.mute(); e.target.playVideo(); } catch (err) {}
          },
        },
      });
    };
    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      if (!document.getElementById("yt-iframe-api")) {
        const tag = document.createElement("script");
        tag.id = "yt-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); createPlayer(); };
    }
    return () => { cancelled = true; try { playerRef.current && playerRef.current.destroy(); } catch (e) {} };
  }, []);

  // THE gesture: a single tap parts the curtains AND unlocks full-volume audio.
  // Because this runs synchronously inside the tap, iOS/Android allow the sound.
  const enter = () => {
    if (open) return;
    setOpen(true);
    const p = playerRef.current;
    if (p && readyRef.current) {
      try { p.unMute(); p.setVolume(100); p.playVideo(); } catch (e) {}
      setMuted(false);
    }
    // Retry shortly in case the player finished loading a beat after the tap.
    setTimeout(() => {
      const pp = playerRef.current;
      if (pp) { try { pp.unMute(); pp.setVolume(100); pp.playVideo(); setMuted(pp.isMuted()); } catch (e) {} }
    }, 400);
  };

  const toggleSound = () => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    try {
      if (muted) { p.unMute(); p.setVolume(100); setMuted(false); }
      else { p.mute(); setMuted(true); }
    } catch (e) {}
  };

  return (
    <div className={`curtain-intro ${open ? "is-open" : ""}`} data-testid="curtain-intro">
      <div className="curtain-stage">
        <div className="curtain-video">
          <div id="curtain-yt-player" data-testid="curtain-pilot-video"></div>
          <div className="curtain-video-shield" onClick={enter}></div>
        </div>
        {open && (
          <button className="curtain-sound-btn" onClick={toggleSound} data-testid="curtain-unmute-btn">
            {muted ? (<><Volume2 size={18} /> Tap for Sound</>) : (<><VolumeX size={18} /> Mute</>)}
          </button>
        )}
      </div>
      <div className="curtain-panel curtain-left" style={{ backgroundImage: "url('/curtain-hero.jpg?v=6')", backgroundSize: "100vw 100%", backgroundPosition: "left center" }}></div>
      <div className="curtain-panel curtain-right" style={{ backgroundImage: "url('/curtain-hero.jpg?v=6')", backgroundSize: "100vw 100%", backgroundPosition: "right center" }}></div>

      {/* Tap-to-enter splash: a single gesture that opens the curtains with sound */}
      {!open && (
        <button className="curtain-enter-splash" onClick={enter} data-testid="curtain-enter-splash">
          <span className="curtain-enter-ring"><Play size={30} fill="currentColor" /></span>
          <span className="curtain-enter-label">Tap to Enter — with Sound</span>
        </button>
      )}

      <button className="curtain-close-btn" onClick={onClose} data-testid="curtain-skip-btn">
        Enter Site <ChevronRight size={18} />
      </button>
    </div>
  );
};


const HomePage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [mainCast, setMainCast] = useState([
    { name: "Dianne Dixon", role: "Lead Cast" },
    { name: "Keisha Martin", role: "Lead Cast" },
    { name: "Wally Winks", role: "Lead Cast" },
    { name: "Althea Hewitt", role: "Lead Cast" },
  ]);

  useEffect(() => {
    const fetchCast = async () => {
      try {
        const res = await axios.get(`${API}/cast`);
        if (res.data.main_cast && res.data.main_cast.length > 0) {
          setMainCast(res.data.main_cast);
        }
      } catch (err) {
        console.error('Failed to fetch cast:', err);
      }
    };
    fetchCast();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail === "Email already subscribed") {
        alert("You're already subscribed!");
      }
    }
    setLoading(false);
  };

  return (
    <div data-testid="home-page">
      {showIntro && <CurtainIntro onClose={() => setShowIntro(false)} />}
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen overflow-hidden">
        {/* Jamaica + USA Flags Background - Way down at the bottom */}
        <div className="absolute left-0 w-full z-0 bottom-0 h-[40%] md:h-[25%]">
          <img 
            src="https://customer-assets.emergentagent.com/job_logo-tallawah/artifacts/zp6krnz5_jamaica-vs-united-states-america-260nw-1203844444.jpg.webp" 
            alt="Jamaica and USA Flags"
            className="w-full h-full object-cover object-top"
            style={{ opacity: 0.6 }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] via-transparent to-[#1A1A1A]/80"></div>
        </div>

        {/* NYC Skyline at Top - Takes more space */}
        <div className="absolute top-0 left-0 w-full h-[50%] md:h-[75%] z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_logo-tallawah/artifacts/g70tg90o_new_york_usa_night_city_119004_2048x1152.jpg" 
            alt="NYC Skyline"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1A1A1A]"></div>
        </div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#1A1A1A]/90 z-15"></div>

        {/* Main Content */}
        <div className="relative z-20 flex flex-col items-center justify-end min-h-screen px-4 pb-12 md:pb-16">
          {/* Gold Logo */}
          <img 
            src="/tallawah-logo-gold.png" 
            alt="Tallawah Show - Jamaicans in NYC"
            className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg h-auto animate-fadeInUp mb-6"
          />
          
          <p className="text-lg md:text-2xl text-gray-300 mb-6 animate-fadeInUp animate-delay-1 font-light tracking-wide text-center">
            Ten characters. One community. Infinite stories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp animate-delay-2">
            <Link to="/watch" className="btn-primary flex items-center justify-center gap-2" data-testid="watch-pilot-btn">
              <Play size={20} /> Watch Pilot
            </Link>
            <Link to="/support" className="btn-secondary" data-testid="support-btn">
              Support the Project
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold gold-accent mb-6">THE STORY</h2>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
            A world-people, multi-cultural show happening in NYC in 2026. TALLAWAH JAMAICANS IN NYC 
            follows strong, fearless, warrior-heart Jamaicans from different fields of business and walks 
            of life — navigating family, world issues, and life in the eyes of Jesus Christ, their anchor 
            and covering, in a time such as now.
          </p>
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: <Heart className="gold-accent" size={40} />, label: "Culture" },
              { icon: <Users className="gold-accent" size={40} />, label: "Family" },
              { icon: <Music className="gold-accent" size={40} />, label: "Music" },
              { icon: <Star className="gold-accent" size={40} />, label: "Identity" },
            ].map((item, i) => (
              <div key={i} className="p-6 border border-[#FEDD00]/20 hover:border-[#FEDD00] transition-all">
                {item.icon}
                <p className="mt-4 text-lg font-semibold">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Cast */}
      <section className="section section-darker">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold gold-accent text-center mb-6">FEATURED CAST</h2>
          <div className="gold-line w-32 mx-auto mb-12"></div>
          <div className="grid md:grid-cols-5 gap-6">
            {mainCast.map((member, i) => (
              <FeaturedCastCard 
                key={i} 
                member={member} 
                onClick={() => setSelectedMember(member)}
              />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/cast" className="btn-secondary inline-flex items-center gap-2">
              View Full Cast <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cast Member Modal */}
      <CastMemberModal 
        member={selectedMember} 
        isOpen={!!selectedMember} 
        onClose={() => setSelectedMember(null)} 
      />

      {/* Video Preview */}
      <section className="section section-green">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">WATCH THE TEASER</h2>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <div className="video-container">
            <iframe 
              src="https://www.youtube.com/embed/zBSRL4lPCAk" 
              title="Tallawah Show Teaser"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              data-testid="teaser-video"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section section-dark">
        <div className="max-w-xl mx-auto px-4 text-center">
          <Mail className="gold-accent mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold gold-accent mb-4">STAY UPDATED</h2>
          <p className="text-gray-400 mb-8">Subscribe to get the latest news, behind-the-scenes content, and release updates.</p>
          {subscribed ? (
            <div className="p-6 border border-[#FEDD00] bg-[#FEDD00]/10">
              <p className="gold-accent font-semibold">Thank you for subscribing!</p>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4" data-testid="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input flex-1"
                required
                data-testid="newsletter-email"
              />
              <button type="submit" className="btn-primary" disabled={loading} data-testid="newsletter-submit">
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const themes = [
    { title: "Culture", description: "The rich heritage of the Jamaican community, from music and food to traditions that guide our characters through life's challenges." },
    { title: "Family", description: "Blood ties and chosen family interweave as characters navigate loyalty, sacrifice, and unconditional love across generations." },
    { title: "Music", description: "From reggae rhythms to gospel choirs, music pulses through every scene, connecting characters to their heritage and each other." },
    { title: "Cultural Identity", description: "Balancing Jamaican roots with American dreams, our characters forge new identities while honoring where they come from." },
  ];

  return (
    <div className="pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">ABOUT THE SHOW</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            An original television series celebrating Jamaican culture, heritage, and community in New York City.
          </p>
        </div>
      </section>

      {/* Synopsis */}
      <section className="section section-dark">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-sm tracking-[0.3em] text-[#FEDD00]/80 uppercase mb-4">A World-People Multi-Cultural Show — Happening in NYC in the Year 2026</p>
          <h2 className="text-3xl font-bold gold-accent mb-4">THE STORY</h2>
          <div className="gold-line-left w-24 mb-8"></div>

          <blockquote className="border-l-4 border-[#FEDD00] pl-6 mb-10">
            <p className="text-xl md:text-2xl text-white italic leading-relaxed">
              "Some very strong and fearless warrior-heart Jamaicans from different fields of business 
              and walks of life, navigating life in the eyes of Jesus Christ — our only Savior and 
              covering — in a time such as now."
            </p>
            <cite className="block mt-4 text-sm text-gray-400 not-italic">— Show Logline</cite>
          </blockquote>

          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            The reality show <span className="gold-accent font-semibold">TALLAWAH JAMAICANS IN NYC</span> takes 
            us into the lives of a few hard-working and super-talented Jamaican folks living in NYC — going 
            through the bustle and hustle of life and maintaining their sanity and composure after surviving 
            the epicenter of COVID-19.
          </p>
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            They deal with their family lives, world issues, and the aftermath of Jamaica being terribly 
            destroyed by Hurricane Melissa. But using their faith and strong trust in Jesus Christ — and 
            their solid foundation in His ways and total teachings — becomes their anchor for living and 
            meeting challenges, while they share it with the world at large, all while living in NYC.
          </p>
          <p className="text-lg text-gray-300 leading-relaxed">
            The word "Tallawah" is Jamaican patois meaning strong, brave, resilient — not to be underestimated. 
            This spirit infuses every character and storyline.
          </p>

          <p className="mt-8 text-base text-gray-400">
            <span className="uppercase tracking-widest text-[#FEDD00]/80 text-sm">Written by</span><br/>
            <span className="text-xl text-white font-semibold">Charline Gager</span>
          </p>
        </div>
      </section>

      {/* Themes */}
      <section className="section section-darker">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent text-center mb-12">THEMES WE EXPLORE</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {themes.map((theme, i) => (
              <div key={i} className="character-card">
                <h3 className="text-2xl font-bold gold-accent mb-3">{theme.title}</h3>
                <p className="text-gray-400">{theme.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator / Writer */}
      <section className="section section-green">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">THE WRITER & CREATOR</h2>
          <div className="gold-line w-32 mx-auto mb-8 bg-black/30"></div>
          <div className="bg-black/20 p-8 rounded flex flex-col items-center">
            <img
              src="/charline-gager.jpg"
              alt="Charline Gager - Writer & Creator"
              className="w-40 h-40 md:w-48 md:h-48 object-cover object-top rounded-full border-4 border-[#FEDD00] shadow-xl mb-6"
              data-testid="creator-charline-photo"
            />
            <h3 className="text-2xl font-bold mb-1">Charline Gager</h3>
            <p className="text-sm uppercase tracking-widest opacity-80 mb-4">Writer &amp; Creator</p>
            <p className="text-lg leading-relaxed opacity-90 max-w-2xl">
              "TALLAWAH is more than a television series — it's a testament of faith. These are strong, 
              fearless, warrior-heart Jamaicans navigating life through the eyes of Jesus Christ. 
              This is our story: faith over fear, in a time such as now."
            </p>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent text-center mb-12">OUR NEW YORK</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {["Harlem", "Brooklyn", "Queens"].map((location, i) => (
              <div key={i} className="relative overflow-hidden group">
                <div className="h-64 bg-gradient-to-t from-black to-transparent relative">
                  <img 
                    src="https://images.unsplash.com/photo-1749292177172-66edfd44e7cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxCcm9va2x5biUyMGJyb3duc3RvbmUlMjBuZWlnaGJvcmhvb2QlMjBzdHJlZXR8ZW58MHx8fHwxNzcyMzI1NzA5fDA&ixlib=rb-4.1.0&q=85"
                    alt={location}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black">
                    <h3 className="text-2xl font-bold gold-accent">{location}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Cast Page
const CastPage = () => {
  const [castData, setCastData] = useState({ main_cast: [], additional_cast: [] });
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const fetchCast = async () => {
      try {
        const res = await axios.get(`${API}/cast`);
        setCastData(res.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchCast();
  }, []);

  // Auto-open a member's profile when arriving from a shared social link (?member=<id>)
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const memberId = params.get('member');
    if (!memberId) return;
    const all = [...(castData.main_cast || []), ...(castData.additional_cast || [])];
    const found = all.find((m) => m.id === memberId);
    if (found) setSelectedMember(found);
  }, [loading, castData]);

  const defaultMainCast = [
    { name: "Naketa Jackson", role: "Lead Cast", bio: "Grammy-nominated talent bringing authentic Jamaican spirit to the screen." },
    { name: "Dianne Dixon", role: "Lead Cast", bio: "A powerful voice representing the heart of the Jamaican diaspora." },
    { name: "Keisha Martin", role: "Lead Cast", bio: "Embodying strength, family, and resilience in every scene." },
    { name: "Wally Winks", role: "Lead Cast", bio: "Bringing humor and heart to the streets of NYC." },
    { name: "Althea Hewitt", role: "Lead Cast", bio: "A commanding presence capturing the essence of Jamaican strength." },
  ];

  const mainCast = castData.main_cast.length > 0 ? castData.main_cast : defaultMainCast;
  const additionalCast = castData.additional_cast.length > 0 ? castData.additional_cast : Array(5).fill({ name: "Cast Member", role: "Supporting Cast" });

  const hasSocialLinks = (member) => member.social_links && Object.values(member.social_links).some(v => v);

  return (
    <div className="pt-24" data-testid="cast-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">CAST & CREW</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Meet the talented voices bringing TALLAWAH to life.
          </p>
        </div>
      </section>

      {/* Main Cast */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent mb-12">MAIN CAST</h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {mainCast.map((member, i) => (
              <div 
                key={i} 
                className="cast-card clickable" 
                data-testid={`main-cast-${i}`}
                onClick={() => setSelectedMember(member)}
              >
                {member.image_url ? (
                  <div className="cast-image-container">
                    <img 
                      src={member.image_url} 
                      alt={member.name}
                      className="cast-image"
                      style={{ objectPosition: member.image_position || 'center top' }}
                    />
                    <div className="cast-card-badge">View Profile</div>
                  </div>
                ) : (
                  <div className="cast-placeholder">
                    <Users size={60} />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold gold-accent mb-2">{member.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{member.role}</p>
                  <p className="text-gray-400 text-sm">{member.bio}</p>
                  {hasSocialLinks(member) && (
                    <div className="flex gap-2 mt-3 justify-center">
                      {member.social_links?.website && <Globe size={16} className="text-gray-500" />}
                      {member.social_links?.instagram && <Instagram size={16} className="text-gray-500" />}
                      {member.social_links?.youtube && <Youtube size={16} className="text-gray-500" />}
                      {member.social_links?.tiktok && <TikTokIcon size={16} className="text-gray-500" />}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cast Member Modal */}
      <CastMemberModal 
        member={selectedMember} 
        isOpen={!!selectedMember} 
        onClose={() => setSelectedMember(null)} 
      />

      {/* Supporting Cast */}
      <section className="section section-darker">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent mb-12">SUPPORTING CAST</h2>
          <div className="grid md:grid-cols-5 gap-6">
            {additionalCast.map((member, i) => {
              const socialLinks = member.social_links || {};
              const hasSocials = Object.values(socialLinks).some(v => v);
              return (
                <div 
                  key={i} 
                  className="cast-card clickable"
                  onClick={() => setSelectedMember(member)}
                >
                  {member.image_url ? (
                    <div className="cast-image-container h-48">
                      <img 
                        src={member.image_url} 
                        alt={member.name}
                        className="cast-image"
                        style={{ objectPosition: member.image_position || 'center top' }}
                      />
                      <div className="cast-card-badge">View Profile</div>
                    </div>
                  ) : (
                    <div className="cast-placeholder h-48">
                      <Users size={40} />
                    </div>
                  )}
                  <div className="p-4 text-center">
                    <h3 className="font-bold gold-accent">{member.name}</h3>
                    <p className="text-gray-500 text-sm">{member.role}</p>
                    {hasSocials && (
                      <div className="flex gap-2 mt-2 justify-center">
                        {socialLinks.website && <Globe size={14} className="text-gray-500" />}
                        {socialLinks.instagram && <Instagram size={14} className="text-gray-500" />}
                        {socialLinks.youtube && <Youtube size={14} className="text-gray-500" />}
                        {socialLinks.tiktok && <TikTokIcon size={14} className="text-gray-500" />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Crew */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent mb-12">CREW</h2>
          <div className="flex justify-center">
            <div className="cast-card max-w-xs" data-testid="crew-charline">
              <div className="cast-image-container">
                <img
                  src="/charline-gager.jpg"
                  alt="Charline Gager"
                  className="cast-image"
                  style={{ objectPosition: 'center top' }}
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold gold-accent mb-1">Charline Gager</h3>
                <p className="text-sm text-gray-500">Writer &amp; Creator</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Stars CTA */}
      <section className="section section-green">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Film size={48} className="mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">WANT TO BE PART OF TALLAWAH?</h2>
          <p className="text-lg mb-8 opacity-90">
            We're always looking for authentic voices to join our production. 
            Guest star opportunities are available for upcoming episodes.
          </p>
          <Link to="/support" className="btn-primary bg-black text-[#FEDD00] hover:bg-white hover:text-black">
            Learn More
          </Link>
        </div>
      </section>
    </div>
  );
};

// Helper to extract YouTube video ID from various URL formats
const extractYouTubeId = (url) => {
  if (!url) return null;
  // Handle youtu.be format
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return shortMatch[1];
  // Handle youtube.com/watch?v= format
  const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return watchMatch[1];
  // Handle youtube.com/embed/ format
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) return embedMatch[1];
  return null;
};

// Video component with thumbnail overlay - click to play
const VideoPlayer = ({ videoId, title, castMember, isMainTeaser }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Use hqdefault as it's more reliable than maxresdefault
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=0&iv_load_policy=3`;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div 
      className={`video-card-wrapper ${isMainTeaser ? 'main-teaser' : ''}`}
      data-testid={isMainTeaser ? 'main-teaser-video' : `cast-video-${castMember?.name?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="video-container">
        {!isPlaying ? (
          <div 
            className="video-thumbnail-overlay"
            onClick={handlePlay}
          >
            <img 
              src={thumbnailUrl} 
              alt={title}
              className="video-thumbnail-img"
            />
            <div className="video-play-center">
              <div className="video-play-btn">
                <Play size={32} fill="#000" />
              </div>
              <span className="video-play-text">Play Video</span>
            </div>
          </div>
        ) : (
          <iframe
            src={embedUrl}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )}
      </div>
      {castMember && (
        <div className="video-cast-info">
          <div className="flex items-center gap-4">
            {castMember.image_url && (
              <img 
                src={castMember.image_url} 
                alt={castMember.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-[#FEDD00]"
              />
            )}
            <div>
              <h3 className="text-lg font-bold gold-accent">{castMember.name}</h3>
              <p className="text-sm text-gray-400">{castMember.role}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Watch Page
const WatchPage = () => {
  const [castVideos, setCastVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCastVideos = async () => {
      try {
        const response = await axios.get(`${API}/cast`);
        const allCast = [...(response.data.main_cast || []), ...(response.data.additional_cast || [])];
        
        // Filter cast members with videos
        const videoCast = allCast.filter(member => {
          const videoUrl = member.video_url || member.social_links?.youtube;
          // Only include if it's a specific video URL, not a channel
          return videoUrl && extractYouTubeId(videoUrl);
        }).map(member => ({
          ...member,
          videoId: extractYouTubeId(member.video_url || member.social_links?.youtube)
        }));
        
        setCastVideos(videoCast);
      } catch (error) {
        console.error('Error fetching cast videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCastVideos();
  }, []);

  return (
    <div className="pt-24" data-testid="watch-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">WATCH</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Experience the world of TALLAWAH through our trailers and artist features.
            <span className="block text-sm text-gray-500 mt-2">Click on any video to play</span>
          </p>
        </div>
      </section>

      {/* Featured Pilot Episode */}
      <section className="section section-dark">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1 bg-[#FEDD00] text-black text-sm font-bold tracking-wider mb-4">
              PILOT EPISODE
            </span>
          </div>
          <VideoPlayer 
            videoId="hihlBQ0Bn84" 
            title="TALLAWAH JAMAICANS IN NYC — The Pilot"
            isMainTeaser={true}
          />
          <div className="text-center mt-8">
            <p className="text-gray-400 max-w-2xl mx-auto">
              Watch the pilot of TALLAWAH JAMAICANS IN NYC — the first look at the strong, 
              fearless, warrior-heart Jamaicans navigating life in New York City through their faith.
            </p>
          </div>
        </div>
      </section>

      {/* Main Teaser Video */}
      <section className="section section-darker">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1 bg-[#FEDD00] text-black text-sm font-bold tracking-wider mb-4">
              OFFICIAL TEASER
            </span>
          </div>
          <VideoPlayer 
            videoId="zBSRL4lPCAk" 
            title="Tallawah Show Official Teaser"
            isMainTeaser={true}
          />
          <div className="text-center mt-8">
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get your first look at TALLAWAH JAMAICANS IN NYC. This teaser introduces you 
              to the world, the characters, and the spirit that drives our story.
            </p>
          </div>
        </div>
      </section>

      {/* Cast Featured Videos */}
      {castVideos.length > 0 && (
        <section className="section section-darker">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold gold-accent text-center mb-4">CAST FEATURES</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Get to know the talented artists behind TALLAWAH through their featured videos.
            </p>
            <div className="space-y-16">
              {castVideos.map((member, index) => (
                <div key={member.id} className="max-w-4xl mx-auto">
                  <VideoPlayer 
                    videoId={member.videoId}
                    title={`${member.name} - ${member.role}`}
                    castMember={member}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Loading state for cast videos */}
      {loading && (
        <section className="section section-darker">
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FEDD00]"></div>
          </div>
        </section>
      )}

      {/* Press Kit & Release Info */}
      <section className="section section-dark">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 border border-[#FEDD00]/30 hover:border-[#FEDD00] transition-colors">
              <Film className="gold-accent mb-4" size={40} />
              <h3 className="text-2xl font-bold gold-accent mb-4">PRESS KIT</h3>
              <p className="text-gray-400 mb-6">
                Download our official press kit with high-resolution images, 
                cast bios, synopsis, and production information.
              </p>
              <button className="btn-secondary">Coming Soon</button>
            </div>
            <div className="p-8 border border-[#FEDD00]/30 hover:border-[#FEDD00] transition-colors">
              <Calendar className="gold-accent mb-4" size={40} />
              <h3 className="text-2xl font-bold gold-accent mb-4">RELEASE INFO</h3>
              <p className="text-gray-400 mb-6">
                Stay tuned for announcements about premiere dates, 
                screening events, and streaming availability.
              </p>
              <Link to="/contact" className="btn-secondary inline-block">Contact for Details</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Behind the Scenes - Placeholder for future content */}
      <section className="section section-darker">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent text-center mb-12">BEHIND THE SCENES</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-video bg-[#2A2A2A] flex items-center justify-center border border-[#FEDD00]/20 hover:border-[#FEDD00]/40 transition-colors">
                <div className="text-center text-gray-500">
                  <Film size={40} className="mx-auto mb-2" />
                  <p>Coming Soon</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Support Page
const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/support/inquiry`, formData);
      setSubmitted(true);
    } catch (error) {
      alert("Error submitting inquiry. Please try again.");
    }
    setLoading(false);
  };

  const opportunities = [
    { 
      title: "Guest Star Participation", 
      description: "Be part of TALLAWAH! We're seeking authentic voices for guest appearances.",
      icon: <Star className="gold-accent" size={32} />
    },
    { 
      title: "Sponsorship Opportunities", 
      description: "Partner with us to reach a diverse, engaged audience passionate about Caribbean culture.",
      icon: <Heart className="gold-accent" size={32} />
    },
    { 
      title: "Product Placement", 
      description: "Feature your brand authentically within our NYC storylines.",
      icon: <Film className="gold-accent" size={32} />
    },
    { 
      title: "Community Partnerships", 
      description: "Join us in celebrating and supporting the Jamaican diaspora community.",
      icon: <Users className="gold-accent" size={32} />
    },
  ];

  return (
    <div className="pt-24" data-testid="support-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">GET INVOLVED</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Join us in bringing TALLAWAH to screens worldwide. There are many ways to be part of this journey.
          </p>
        </div>
      </section>

      {/* Opportunities */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {opportunities.map((opp, i) => (
              <div key={i} className="p-8 border border-[#FEDD00]/20 hover:border-[#FEDD00] transition-all">
                {opp.icon}
                <h3 className="text-2xl font-bold gold-accent mt-4 mb-3">{opp.title}</h3>
                <p className="text-gray-400">{opp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="section section-darker">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold gold-accent text-center mb-8">SUBMIT AN INQUIRY</h2>
          {submitted ? (
            <div className="p-8 border border-[#FEDD00] bg-[#FEDD00]/10 text-center">
              <h3 className="text-2xl font-bold gold-accent mb-4">Thank You!</h3>
              <p className="text-gray-300">We've received your inquiry and will be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="support-form">
              <div className="grid md:grid-cols-2 gap-6">
                <input 
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="form-input"
                  required
                  data-testid="support-name"
                />
                <input 
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-input"
                  required
                  data-testid="support-email"
                />
              </div>
              <input 
                type="text"
                placeholder="Company/Organization (Optional)"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="form-input"
                data-testid="support-company"
              />
              <select 
                value={formData.inquiry_type}
                onChange={(e) => setFormData({...formData, inquiry_type: e.target.value})}
                className="form-input"
                data-testid="support-type"
              >
                <option value="general">General Inquiry</option>
                <option value="guest_star">Guest Star Opportunity</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="product_placement">Product Placement</option>
                <option value="partnership">Community Partnership</option>
                <option value="donation">Donation</option>
              </select>
              <textarea 
                placeholder="Your Message *"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="form-input min-h-[150px]"
                required
                data-testid="support-message"
              />
              <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="support-submit">
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

// News Page
const NewsPage = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setNewsItems(response.data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="pt-24" data-testid="news-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">NEWS & PRESS</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stay updated with the latest news, press releases, and announcements.
          </p>
        </div>
      </section>

      {/* News Grid */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FEDD00]"></div>
            </div>
          ) : newsItems.length === 0 ? (
            <div className="text-center py-16">
              <Film className="gold-accent mx-auto mb-4" size={64} />
              <h3 className="text-2xl font-bold gold-accent mb-3">Coming Soon</h3>
              <p className="text-gray-400 max-w-md mx-auto">
                Exciting news and press releases are on the way. Subscribe to our newsletter to stay updated!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {newsItems.map((item, i) => (
                <article key={item.id || i} className="news-card" data-testid={`news-item-${i}`}>
                  {item.image_url ? (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#006847]/30 to-[#1A1A1A] flex items-center justify-center">
                      <Film className="gold-accent" size={48} />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-xs gold-accent font-semibold tracking-wider uppercase">{item.category}</span>
                    <h3 className="text-xl font-bold mt-2 mb-3">{item.title}</h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">{item.content}</p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <Clock size={14} className="mr-2" />
                      {formatDate(item.published_at)}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Press Contact */}
      <section className="section section-darker">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold gold-accent mb-6">PRESS INQUIRIES</h2>
          <p className="text-gray-400 mb-8">
            For media inquiries, interview requests, and press materials, please contact our press team.
          </p>
          <div className="inline-flex items-center gap-3 px-8 py-4 border border-[#FEDD00]">
            <Mail className="gold-accent" />
            <span className="text-lg">press@tallawahshow.com</span>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiry_type: 'general'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (error) {
      alert("Error submitting message. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold gold-accent mb-6">CONTACT US</h1>
          <div className="gold-line w-32 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We'd love to hear from you. Get in touch with the TALLAWAH team.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section section-dark">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold gold-accent mb-4">GENERAL INQUIRIES</h3>
                <div className="flex items-center gap-3 text-gray-400">
                  <Mail className="gold-accent" size={20} />
                  <span>info@tallawahshow.com</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold gold-accent mb-4">PRESS</h3>
                <div className="flex items-center gap-3 text-gray-400">
                  <Mail className="gold-accent" size={20} />
                  <span>press@tallawahshow.com</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold gold-accent mb-4">FOLLOW US</h3>
                <div className="flex gap-4 flex-wrap">
                  <a href="https://www.instagram.com/tallawah.ja2us/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors"><Instagram size={28} /></a>
                  <a href="https://x.com/TALLAWAHSHOW" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors"><Twitter size={28} /></a>
                  <a href="https://www.facebook.com/profile.php?id=100083257970568" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors"><Facebook size={28} /></a>
                  <a href="https://www.youtube.com/channel/UCyuz1dtiIA3NwepDoeGlYkw" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors"><Youtube size={28} /></a>
                  <a href="https://tiktok.com/@tallawah962" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                  </a>
                  <a href="https://linkedin.com/in/tallawah-show-64037b3b2" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FEDD00] transition-colors"><Linkedin size={28} /></a>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold gold-accent mb-4">LOCATION</h3>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin className="gold-accent mt-1" size={20} />
                  <span>New York City<br />United States</span>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              {submitted ? (
                <div className="p-8 border border-[#FEDD00] bg-[#FEDD00]/10 text-center">
                  <h3 className="text-2xl font-bold gold-accent mb-4">Message Sent!</h3>
                  <p className="text-gray-300">Thank you for reaching out. We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <input 
                      type="text"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="form-input"
                      required
                      data-testid="contact-name"
                    />
                    <input 
                      type="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="form-input"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  <select 
                    value={formData.inquiry_type}
                    onChange={(e) => setFormData({...formData, inquiry_type: e.target.value})}
                    className="form-input"
                    data-testid="contact-type"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="press">Press/Media</option>
                    <option value="partnership">Partnership</option>
                    <option value="casting">Casting</option>
                    <option value="other">Other</option>
                  </select>
                  <input 
                    type="text"
                    placeholder="Subject *"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="form-input"
                    required
                    data-testid="contact-subject"
                  />
                  <textarea 
                    placeholder="Your Message *"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="form-input min-h-[180px]"
                    required
                    data-testid="contact-message"
                  />
                  <button type="submit" className="btn-primary" disabled={loading} data-testid="contact-submit">
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Component
function App() {
  // Show Under Construction page if enabled
  if (UNDER_CONSTRUCTION) {
    return (
      <div className="App min-h-screen bg-[#1A1A1A]">
        <UnderConstructionPage />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-[#1A1A1A]">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/cast" element={<CastPage />} />
          <Route path="/watch" element={<WatchPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/dataroom" element={<DataRoom />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
